/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useEffect, useMemo } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Select, Spin, Typography } from '@coze-arch/coze-design';

import { useOntologyApps } from './use-ontology-apps';
import { isSrMappingSelectionInvalid } from './sr-mapping-selection';
import {
  excludeSrMappedApps,
  filterSrMappedApps,
} from './ontology-apps-service';

const ontologyQueryClient = new QueryClient();

export interface OntologyAppSelectValue {
  appId?: string;
  appName?: string;
}

export interface OntologyAppSelectProps {
  value?: OntologyAppSelectValue;
  onChange?: (value: OntologyAppSelectValue) => void;
  readonly?: boolean;
  validateStatus?: 'default' | 'error' | 'warning';
  className?: string;
  /** 本体数仓模式：只列出已发布数仓映射的应用 */
  requireSrMapping?: boolean;
  /** 本体模型模式：排除已发布数仓映射的应用 */
  excludeSrMapping?: boolean;
  /**
   * 已选应用不在当前模式的应用列表中时为 true（仅展示为无效，不清空存储值）。
   * 列表加载完成后上报；卸载时复位为 false。
   */
  onSrMappingInvalidChange?: (invalid: boolean) => void;
}

const OntologyAppSelectInner: FC<OntologyAppSelectProps> = ({
  value,
  onChange,
  readonly,
  validateStatus,
  className,
  requireSrMapping,
  excludeSrMapping,
  onSrMappingInvalidChange,
}) => {
  const { data: allApps = [], isLoading, isError } = useOntologyApps();
  const apps = useMemo(() => {
    if (requireSrMapping) {
      return filterSrMappedApps(allApps);
    }
    if (excludeSrMapping) {
      return excludeSrMappedApps(allApps);
    }
    return allApps;
  }, [allApps, requireSrMapping, excludeSrMapping]);

  const options = useMemo(
    () =>
      apps.map(app => ({
        label: app.name,
        value: app.app_id,
      })),
    [apps],
  );

  const srMappingInvalid = isSrMappingSelectionInvalid({
    requireSrMapping,
    excludeSrMapping,
    // 列表加载失败时 apps 为空，按「加载中」处理，避免把已选应用误判为无效
    loading: isLoading || isError,
    apps,
    appId: value?.appId,
  });

  useEffect(() => {
    onSrMappingInvalidChange?.(srMappingInvalid);
    return () => onSrMappingInvalidChange?.(false);
  }, [srMappingInvalid, onSrMappingInvalidChange]);

  if (isLoading) {
    return <Spin size="small" />;
  }

  if (isError) {
    return (
      <Typography.Text type="danger" size="small">
        本体应用加载失败
      </Typography.Text>
    );
  }

  return (
    <Select
      className={className}
      value={srMappingInvalid ? undefined : value?.appId}
      disabled={readonly}
      validateStatus={
        srMappingInvalid && !readonly ? 'error' : validateStatus
      }
      placeholder={
        srMappingInvalid
          ? requireSrMapping
            ? '该应用未发布数仓映射，请重新选择'
            : '该应用已发布数仓映射，请重新选择'
          : requireSrMapping && apps.length === 0
            ? '暂无已发布数仓映射的本体应用'
            : excludeSrMapping && apps.length === 0
              ? '暂无可用的本体应用'
              : '请选择本体应用'
      }
      optionList={options}
      style={{ width: '100%' }}
      onChange={appId => {
        const selected = apps.find(app => app.app_id === appId);
        onChange?.({
          appId: appId as string,
          appName: selected?.name,
        });
      }}
      filter
      showClear
    />
  );
};

/** Agent IDE 等场景无外层 QueryClientProvider，组件内自带 Provider */
export const OntologyAppSelect: FC<OntologyAppSelectProps> = props => (
  <QueryClientProvider client={ontologyQueryClient}>
    <OntologyAppSelectInner {...props} />
  </QueryClientProvider>
);
